import type {
  CampoDetalhe,
  CriterioDetalhe,
  GrupoDetalhe,
  PerguntaDetalhe,
  SolicitacaoDetalhe,
} from "../../services";
import type {
  AdimplenciaAssociadoInitial,
  AdimplenciaAssociadoMeta,
  SecaoDocumento,
  SomenteAnalistaVariant,
  TipoSecaoDocumento,
} from "./models";

export function extrairNomeArquivoParaExibicao(arquivo: unknown): string | undefined {
  if (!arquivo || typeof arquivo !== "object") return undefined;
  const a = arquivo as Record<string, unknown>;
  const candidatos = [
    a.nomeOriginalUsuario,
    a.nomeOriginal,
    a.nomeUsuarioEnvio,
    a.nomeExibicao,
    a.nomeUploadOriginal,
  ];
  for (const c of candidatos) {
    const s = String(c ?? "").trim();
    if (s) return s;
  }
  return undefined;
}

// GET detalhe da solicitação: o backend pode enviar `opcoesSelecionadas` ou
// `opcoesSelecionadasIds`; em alguns casos só preenche `opcaoCampoId` e deixa os arrays vazios
// (o PATCH ainda envia `opcoesSelecionadasIds` + `opcaoCampoId`).
export function readOpcoesSelecionadasIdsFromResposta(
  resposta: unknown,
): number[] {
  if (!resposta || typeof resposta !== "object") return [];
  const r = resposta as Record<string, unknown>;
  const raw = r.opcoesSelecionadasIds ?? r.opcoesSelecionadas;
  if (Array.isArray(raw)) {
    const ids = raw.filter((id): id is number => typeof id === "number");
    if (ids.length > 0) return ids;
  }
  const opcaoCampoId = r.opcaoCampoId;
  if (typeof opcaoCampoId === "number" && opcaoCampoId > 0) {
    return [opcaoCampoId];
  }
  return [];
}

// Normaliza texto da API / UI para o valor usado nos rádios SIM | NÃO (V2).
export function normalizarValorSimNaoParaUI(raw: unknown): "SIM" | "NÃO" | "" {
  const s = String(raw ?? "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();
  if (!s) return "";
  if (s === "SIM" || s.startsWith("SIM")) return "SIM";
  if (s === "NAO" || s.startsWith("NAO")) return "NÃO";
  // Frases da API (“… cumprir o critério … Sim”) ou rótulos fora do padrão
  if (/\bNAO\b/.test(s)) return "NÃO";
  if (/\bSIM\b/.test(s)) return "SIM";
  return "";
}

function simNaoValorParaOpcaoPergunta(
  descricao: string,
  perguntaEhSimNao: boolean,
): string {
  if (!perguntaEhSimNao) return String(descricao ?? "");
  const sn = normalizarValorSimNaoParaUI(descricao);
  return sn || String(descricao ?? "");
}

function normChaveOpcaoApi(s: string): string {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

// Converte descrição da API para os valores fixos do radio de forma de cobrança (Adimplência V2).
export function mapDescricaoFormaCobrancaBackendParaValorUI(
  descricao: string,
): string {
  const d = normChaveOpcaoApi(descricao);
  if (!d) return "";
  if (d.includes("trimestral")) return "Trimestral";
  if (d.includes("semestral")) return "Semestral";
  if (d.includes("anual")) return "Anual";
  if (d.includes("mensal")) return "Mensal";
  return String(descricao).trim();
}

// Extrai o número do radio de quantidade (0–10) a partir da descrição da API ("10 ou mais", etc.).
export function mapDescricaoQuantidadeBackendParaValorUI(
  descricao: string,
): string {
  const m = String(descricao || "").match(/(\d+)/);
  if (!m) return "";
  const n = parseInt(m[1], 10);
  if (!Number.isFinite(n)) return "";
  return String(Math.min(10, Math.max(0, n)));
}

export function normalizarTituloGrupo(raw: string | null | undefined): string {
  const v = String(raw || "").toLowerCase();
  if (v.includes("obrig")) return "obrigatorios";
  if (v.includes("bonus extra") || v.includes("bônus extra"))
    return "bonus-extra";
  if (v.includes("bonus") || v.includes("bônus")) return "bonus";
  return "obrigatorios";
}


export function classifySomenteAnalistaVariant(
  tituloRaw: string | null | undefined,
): SomenteAnalistaVariant | null {
  const t = String(tituloRaw || "").toLowerCase();
  if (
    t.includes("receita operacional anual") ||
    t.includes("arrecadação mínima mensal") ||
    t.includes("arrecadacao minima mensal")
  ) {
    return "receita-arrecadacao";
  }
  if (
    t.includes("evolução receita operacional") ||
    t.includes("evolucao receita operacional")
  ) {
    return "evolucao-receita";
  }
  if (
    t.includes("evolução do associativismo") ||
    t.includes("evolucao do associativismo")
  ) {
    return "evolucao-associativismo";
  }
  return null;
}

export function mapCampoToTipoSecao(
  campos: CampoDetalhe[],
): TipoSecaoDocumento {
  const normalizeTipo = (c: CampoDetalhe) =>
    String((c as any)?.tipo?.nome || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();

  const hasUpload = campos.some((c) => normalizeTipo(c) === "upload");
  const hasRadio = campos.some((c) => normalizeTipo(c) === "radio");

  if (hasUpload && hasRadio) return "pergunta-com-upload";
  if (hasUpload) return "upload";
  return "pergunta";
}

export function mapGrupoToSecoes(
  grupo: GrupoDetalhe,
  options?: { incluirCriteriosSomenteAnalista?: boolean },
): SecaoDocumento[] {
  const resolveReferenceLinkByTitulo = (titulo: unknown): string | null => {
    const t = String(titulo || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    if (t.includes("dre") && t.includes("demonstracao") && t.includes("resultado")) {
      return "https://docs.google.com/spreadsheets/d/1cxh6o4-Xl0wiFii091PZIrVVQ6lzkLkp/edit?gid=1907858408#gid=1907858408";
    }
    if (t.includes("ata") && t.includes("prestacao") && t.includes("contas")) {
      return "https://docs.google.com/document/d/185o2080G6Fn2ZC_0qANup2dDVU03eKaM/edit";
    }
    if (t.includes("relacao") && t.includes("empresas") && t.includes("associadas")) {
      return "https://docs.google.com/spreadsheets/d/1OPh8pgL-IOcDxGfYgpehI9aluqSvxgZR/edit?gid=859316813#gid=859316813";
    }
    if (t.includes("plano") && t.includes("negocio") && t.includes("sindical")) {
      return "https://docs.google.com/document/d/1vjm3cXjkbc36z61XiOQXOn8yId5EC7_U/edit";
    }
    return null;
  };

  const secoes: SecaoDocumento[] = [];

  grupo.criterios.forEach((criterio: CriterioDetalhe) => {
    const variantAnalista = classifySomenteAnalistaVariant(criterio.titulo);
    if (variantAnalista) {
      if (options?.incluirCriteriosSomenteAnalista) {
        secoes.push({
          id: String((criterio as any).id),
          criterioSolicitacaoId: (criterio as any).id,
          titulo:
            (criterio as any).titulo ||
            `Critério ${(criterio as any).id}`,
          tipo: "somente-analista",
          somenteAnalistaVariant: variantAnalista,
          canEditPergunta: false,
          canEditUpload: false,
          canEditObservacao: false,
        });
      }
      return;
    }

    const tituloCriterio = String(criterio.titulo || "").toLowerCase();

    // Seção especial: Adimplência financeira das empresas associadas.
    // A V2 do front precisa dos campoIds corretos por associado para upload/delete/PATCH.
    if (
      tituloCriterio.includes("adimpl") &&
      Array.isArray((criterio as any).perguntas)
    ) {
      const perguntas: PerguntaDetalhe[] = (criterio as any).perguntas ?? [];

      const startsWithAssociado = (t: unknown) =>
        String(t || "")
          .trim()
          .toLowerCase()
          .startsWith("associado");

      const perguntasNaoAssociadas = perguntas.filter(
        (p) => !startsWithAssociado((p as any).titulo),
      );
      const perguntasAssociadas = perguntas
        .filter((p) => startsWithAssociado((p as any).titulo))
        .sort((a: any, b: any) => (a.ordem ?? 0) - (b.ordem ?? 0));

      const normalizarTipoNome = (campo: any) =>
        String(campo?.tipo?.nome || "")
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "-");

      const valoresString = (v: any) => (v == null ? "" : String(v));

      const obterCampoPorTipo = (pergunta: any, tipoNomeLower: string) =>
        (pergunta?.campos ?? []).find(
          (c: any) => normalizarTipoNome(c) === tipoNomeLower,
        );

      const obterTextoObservacao = (pergunta: any) =>
        (pergunta?.campos ?? []).find(
          (c: any) =>
            normalizarTipoNome(c) === "texto" &&
            String(c?.titulo || "")
              .toLowerCase()
              .includes("observ"),
        );

      // Pelo seu GET, a ordem costuma ser:
      // 1) forma de cobrança + observação geral
      // 2) quantidade de associados
      // 3) tipo do documento
      const perguntaFormaCobranca = perguntasNaoAssociadas[0];
      const perguntaQuantidade = perguntasNaoAssociadas[1];
      const perguntaTipoDocumento = perguntasNaoAssociadas[2];

      const campoFormaCobrancaRadio = obterCampoPorTipo(
        perguntaFormaCobranca,
        "radio",
      );
      const campoFormaCobrancaObservacaoGeral =
        obterTextoObservacao(perguntaFormaCobranca) ??
        (perguntaFormaCobranca?.campos ?? []).find(
          (c: any) => normalizarTipoNome(c) === "texto",
        );

      const campoQuantidadeRadio = obterCampoPorTipo(
        perguntaQuantidade,
        "radio",
      );
      const campoTipoDocumentoRadio = obterCampoPorTipo(
        perguntaTipoDocumento,
        "radio",
      );

      const opcoesRadio = (campo: any) =>
        Array.isArray(campo?.opcoes) ? campo.opcoes : [];

      const construirOpcaoIdByDescricao = (campo: any) => {
        const mapa: Record<string, number> = {};
        opcoesRadio(campo).forEach((o: any) => {
          if (typeof o?.id === "number")
            mapa[String(o?.descricao ?? "")] = o.id;
        });
        return mapa;
      };

      const obterOpcaoDescricaoSelecionada = (campoRadio: any) => {
        const selecionados = readOpcoesSelecionadasIdsFromResposta(
          campoRadio?.resposta,
        );
        const opcoes = opcoesRadio(campoRadio);
        const idSelecionado = selecionados[0];
        const opt = opcoes.find((o: any) => o?.id === idSelecionado);
        return opt?.descricao ? String(opt.descricao) : "";
      };

      const formaCobrancaOpcaoIdByDescricao = construirOpcaoIdByDescricao(
        campoFormaCobrancaRadio,
      );
      const quantidadeOpcaoIdByDescricao =
        construirOpcaoIdByDescricao(campoQuantidadeRadio);

      const formaOpcaoIdByValorUI: Record<string, number> = {};
      for (const [desc, id] of Object.entries(
        formaCobrancaOpcaoIdByDescricao,
      )) {
        const ui = mapDescricaoFormaCobrancaBackendParaValorUI(desc);
        if (ui) formaOpcaoIdByValorUI[ui] = id;
      }

      const quantidadeOpcaoIdByValorUI: Record<string, number> = {};
      for (const [desc, id] of Object.entries(quantidadeOpcaoIdByDescricao)) {
        const ui = mapDescricaoQuantidadeBackendParaValorUI(desc);
        if (ui !== "") quantidadeOpcaoIdByValorUI[ui] = id;
      }

      const tipoDocumentoOpcaoIdByDescricaoBackend =
        construirOpcaoIdByDescricao(campoTipoDocumentoRadio);

      const descricaoBackendToValorUI = (descricaoBackend: string) => {
        const d = String(descricaoBackend || "").toLowerCase();
        if (d.includes("hi gestor")) return "RelatorioDebitos";
        if (d.includes("banco")) return "LiquidacaoDebitos";
        return "";
      };

      const inicialFormaDescricao = obterOpcaoDescricaoSelecionada(
        campoFormaCobrancaRadio,
      );
      const inicialQuantidadeDescricao =
        obterOpcaoDescricaoSelecionada(campoQuantidadeRadio);
      const inicialTipoDocumentoDescricao = obterOpcaoDescricaoSelecionada(
        campoTipoDocumentoRadio,
      );

      const initialFormaCobrancaValorUI = inicialFormaDescricao
        ? mapDescricaoFormaCobrancaBackendParaValorUI(inicialFormaDescricao)
        : "";
      const initialQuantidadeAssociados = (() => {
        const n = parseInt(String(inicialQuantidadeDescricao || "").trim(), 10);
        return Number.isFinite(n) ? Math.min(10, Math.max(0, n)) : 0;
      })();

      const initialTipoDocumentoValorUI = descricaoBackendToValorUI(
        inicialTipoDocumentoDescricao,
      );

      const campoObservacaoGeralId = (campoFormaCobrancaObservacaoGeral as any)
        ?.id;

      const maxAssociados = Math.min(10, perguntasAssociadas.length);

      const associados: AdimplenciaAssociadoMeta[] = [];
      const associadosInitial: AdimplenciaAssociadoInitial[] = [];

      for (let i = 0; i < maxAssociados; i++) {
        const pAssoc = perguntasAssociadas[i] as any;
        const camposAssoc = pAssoc?.campos ?? [];

        const campoCnpj = camposAssoc.find(
          (c: any) => normalizarTipoNome(c) === "cnpj",
        );
        const campoRazaoSocial = camposAssoc.find(
          (c: any) =>
            normalizarTipoNome(c) === "texto" &&
            String(c?.titulo || "")
              .toLowerCase()
              .includes("raz") &&
            String(c?.titulo || "")
              .toLowerCase()
              .includes("social"),
        );
        const campoObs = camposAssoc.find(
          (c: any) =>
            normalizarTipoNome(c) === "texto" &&
            String(c?.titulo || "")
              .toLowerCase()
              .includes("observ"),
        );
        const campoUpload = camposAssoc.find((c: any) =>
          String(normalizarTipoNome(c)).includes("upload"),
        );

        const campoUploadId = (campoUpload as any)?.id;
        const campoCnpjId = (campoCnpj as any)?.id;
        const campoRazaoId = (campoRazaoSocial as any)?.id;
        const campoObservacaoId = (campoObs as any)?.id;

        // Só monta se tiver ids essenciais; evita quebra se o backend variar.
        if (
          typeof campoUploadId !== "number" ||
          typeof campoCnpjId !== "number" ||
          typeof campoRazaoId !== "number" ||
          typeof campoObservacaoId !== "number"
        ) {
          continue;
        }

        associados.push({
          campoUploadId,
          campoCnpjId,
          campoRazaoId,
          campoObservacaoId,
        });

        const cnpj = valoresString(campoCnpj?.resposta?.valorTexto);
        const nomeSocial = valoresString(
          campoRazaoSocial?.resposta?.valorTexto,
        );
        const observacao = valoresString(campoObs?.resposta?.valorTexto);
        const arquivosIds = (campoUpload?.resposta?.arquivos ?? [])
          .map((a: any) => a?.id)
          .filter((id: any) => typeof id === "number");
        const arq0 = (campoUpload?.resposta?.arquivos ?? [])?.[0];
        const arquivoNome = String(arq0?.nome ?? "");
        const arquivoNomeExibicao = extrairNomeArquivoParaExibicao(arq0);

        associadosInitial.push({
          cnpj,
          nomeSocial,
          observacao,
          arquivosIds,
          arquivoNome,
          ...(arquivoNomeExibicao ? { arquivoNomeExibicao } : {}),
        });
      }

      const adimplenciaMeta = {
        campoFormaCobranca: {
          campoId: (campoFormaCobrancaRadio as any)?.id,
          opcaoIdByDescricao: formaCobrancaOpcaoIdByDescricao,
          opcaoIdByValorUI: formaOpcaoIdByValorUI,
        },
        campoQuantidade: {
          campoId: (campoQuantidadeRadio as any)?.id,
          opcaoIdByDescricao: quantidadeOpcaoIdByDescricao,
          opcaoIdByValorUI: quantidadeOpcaoIdByValorUI,
        },
        campoTipoDocumento: {
          campoId: (campoTipoDocumentoRadio as any)?.id,
          opcaoIdByDescricaoBackend: tipoDocumentoOpcaoIdByDescricaoBackend,
          descricaoBackendByValorUI: {
            RelatorioDebitos:
              Object.keys(tipoDocumentoOpcaoIdByDescricaoBackend).find((d) =>
                String(d).toLowerCase().includes("hi gestor"),
              ) ?? "",
            LiquidacaoDebitos:
              Object.keys(tipoDocumentoOpcaoIdByDescricaoBackend).find((d) =>
                String(d).toLowerCase().includes("banco"),
              ) ?? "",
          },
        },
        campoObservacaoGeralId: campoObservacaoGeralId,
        maxAssociados: 10,
        associados,
        initial: {
          formaCobrancaValorUI: initialFormaCobrancaValorUI,
          observacaoGeral: valoresString(
            campoFormaCobrancaObservacaoGeral?.resposta?.valorTexto,
          ),
          quantidadeAssociados: initialQuantidadeAssociados,
          tipoDocumentoValorUI: initialTipoDocumentoValorUI,
          associados: associadosInitial,
        },
      } as any;

      const secao: SecaoDocumento = {
        id: "adimplencia",
        criterioSolicitacaoId: (criterio as any).id,
        titulo: String(criterio.titulo || "Adimplência"),
        tipo: "pergunta",
        adimplenciaMeta,
        canEditPergunta: true,
      } as any;

      // Gera somente essa seção (não cria múltiplas seções genéricas para este critério).
      secoes.push(secao);
      return;
    }

    const perguntasCriterio: any[] = Array.isArray((criterio as any).perguntas)
      ? (criterio as any).perguntas
      : [];

    const obterCamposPergunta = (p: any): any[] => {
      if (Array.isArray(p?.campos)) return p.campos;
      if (Array.isArray(p?.campo)) return p.campo;
      return [];
    };

    const perguntaComCampos = perguntasCriterio.find(
      (p: any) => obterCamposPergunta(p).length > 0,
    );

    const camposNoCriterio = Array.isArray((criterio as any).campos)
      ? (criterio as any).campos
      : Array.isArray((criterio as any).campo)
        ? (criterio as any).campo
        : [];

    const perguntaPrincipal: any =
      perguntaComCampos ??
      (perguntasCriterio.length > 0 ? perguntasCriterio[0] : undefined) ??
      (camposNoCriterio.length > 0
        ? {
            titulo:
              (criterio as any)?.pergunta ?? (criterio as any)?.titulo ?? "",
            campos: camposNoCriterio,
          }
        : undefined);
    if (!perguntaPrincipal) return;

    const campos = obterCamposPergunta(perguntaPrincipal);
    if (!campos.length) return;

    const tipoSecao = mapCampoToTipoSecao(campos);

    const campoComOpcoes = campos.find(
      (c: any) => Array.isArray(c.opcoes) && c.opcoes.length > 0,
    );
    const normalizeTipoNome = (campo: any) =>
      String(campo?.tipo?.nome || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "-");

    const campoPergunta =
      campos.find((c: any) => normalizeTipoNome(c) === "radio") ??
      campos.find(
        (c: any) => Array.isArray(c?.opcoes) && c.opcoes.length > 0,
      ) ??
      campos.find((c: any) => {
        const t = normalizeTipoNome(c);
        if (t.includes("upload")) return false;
        const r = c?.resposta;
        return (
          typeof r?.opcaoCampoId === "number" ||
          Array.isArray(r?.opcoesSelecionadas) ||
          Array.isArray(r?.opcoesSelecionadasIds) ||
          typeof r?.valorTexto === "string"
        );
      });
    const camposUpload = campos.filter((c: any) => {
      const t = String(c?.tipo?.nome || "").toLowerCase();
      return t.includes("upload");
    });
    const campoUpload = camposUpload[0];
    const campoObservacao = campos.find((c: any) =>
      String(c?.titulo || "")
        .toLowerCase()
        .includes("observa"),
    );

    const extrairMaxSizeMB = (v: unknown): number | undefined => {
      if (v == null) return undefined;
      const readNumber = (x: any): number | undefined => {
        if (typeof x === "number" && Number.isFinite(x) && x > 0) return x;
        if (typeof x === "string") {
          const n = parseFloat(x.replace(",", "."));
          return Number.isFinite(n) && n > 0 ? n : undefined;
        }
        return undefined;
      };

      // Candidatos mais comuns que o backend pode retornar.
      const candidates = [
        (v as any).maxSizeMB,
        (v as any).maxSize,
        (v as any).tamanhoMaxMB,
        (v as any).tamanhoMaximoMB,
        (v as any).maxUploadMB,
        (v as any).maxUploadSizeMB,
      ];

      for (const c of candidates) {
        const num = readNumber(c);
        if (!num) continue;

        // Se vier em bytes (ex.: 2097152), converte para MB.
        if (num > 1024 * 1024) {
          const mb = num / (1024 * 1024);
          if (Number.isFinite(mb) && mb > 0 && mb < 10000) return mb;
        }

        return num;
      }

      return undefined;
    };

    const perguntaTexto =
      (perguntaPrincipal as any).titulo?.trim?.() || criterio.titulo || "";

    const opcoesFonteApi = campoComOpcoes?.opcoes?.length
      ? campoComOpcoes.opcoes
      : Array.isArray((campoPergunta as any)?.opcoes) &&
          (campoPergunta as any).opcoes.length > 0
        ? (campoPergunta as any).opcoes
        : [];

    const perguntaEhSimNao =
      /deseja cumprir|utilizou integralmente|sim\/nao|sim ou nao/i.test(
        perguntaTexto,
      );

    const opcoesBackend = opcoesFonteApi.map((o: any) => {
      const desc = String(o?.descricao ?? "");
      return {
        id: o.id,
        valor: simNaoValorParaOpcaoPergunta(desc, perguntaEhSimNao),
        label: desc,
        link: o.link ?? null,
      };
    });

    const precisaFallbackSimNao =
      (tipoSecao === "pergunta" || tipoSecao === "pergunta-com-upload") &&
      (!!campoPergunta || perguntaEhSimNao) &&
      opcoesBackend.length === 0;

    const opcoes = precisaFallbackSimNao
      ? [
          { id: undefined, valor: "SIM", label: "SIM", link: null },
          { id: undefined, valor: "NÃO", label: "NÃO", link: null },
        ]
      : opcoesBackend;

    const respostaRadio = (campoPergunta as any)?.resposta;
    const idsDaRespostaRadio =
      readOpcoesSelecionadasIdsFromResposta(respostaRadio);
    const initialPerguntaOpcaoIds: number[] =
      idsDaRespostaRadio.length > 0
        ? idsDaRespostaRadio
        : readOpcoesSelecionadasIdsFromResposta(campoComOpcoes?.resposta);

    const initialPerguntaValorMappedById =
      initialPerguntaOpcaoIds.length > 0
        ? (opcoes.find((o) => initialPerguntaOpcaoIds.includes(o.id ?? -1))
            ?.valor ?? "")
        : "";

    const initialPerguntaValorPorDescricaoApi =
      initialPerguntaOpcaoIds.length === 1 && opcoesFonteApi.length > 0
        ? normalizarValorSimNaoParaUI(
            opcoesFonteApi.find((o: any) => o.id === initialPerguntaOpcaoIds[0])
              ?.descricao,
          )
        : "";

    const initialPerguntaValorFromTexto = normalizarValorSimNaoParaUI(
      (campoPergunta as any)?.resposta?.valorTexto,
    );

    const initialPerguntaValorFromBoolean = (() => {
      if (!perguntaEhSimNao) return "";
      const b = (respostaRadio as any)?.valorBooleano;
      if (b === true) return "SIM";
      if (b === false) return "NÃO";
      return "";
    })();

    const initialPerguntaValor =
      initialPerguntaValorMappedById ||
      initialPerguntaValorFromTexto ||
      initialPerguntaValorPorDescricaoApi ||
      initialPerguntaValorFromBoolean;

    const arquivosResp = (campoUpload?.resposta as any)?.arquivos;
    const firstArq = Array.isArray(arquivosResp) ? arquivosResp[0] : undefined;

    const initialArquivosIds: number[] =
      (campoUpload?.resposta as any)?.arquivos?.map((a: any) => a.id) ?? [];

    const initialArquivoName: string = firstArq?.nome ?? "";
    const initialArquivoNomeExibicao = extrairNomeArquivoParaExibicao(firstArq);
    const initialObservacao: string = String(
      (campoObservacao?.resposta as any)?.valorTexto ?? "",
    );

    const secao: SecaoDocumento = {
      id: String((criterio as any).id),
      criterioSolicitacaoId: (criterio as any).id,
      titulo:
        (criterio as any).titulo ||
        perguntaTexto ||
        `Critério ${(criterio as any).id}`,
      linkModelo:
        (criterio as any).linkModelo ??
        (criterio as any).link ??
        resolveReferenceLinkByTitulo((criterio as any).titulo) ??
        undefined,
      tipo: tipoSecao,
      pergunta: tipoSecao === "upload" ? undefined : perguntaTexto,
      opcoes:
        tipoSecao === "pergunta" || tipoSecao === "pergunta-com-upload"
          ? opcoes
          : undefined,
      campoPerguntaId: (campoPergunta as any)?.id,
      campoUploadId: (campoUpload as any)?.id,
      campoObservacaoId: (campoObservacao as any)?.id,
      initialPerguntaValor:
        tipoSecao === "pergunta" || tipoSecao === "pergunta-com-upload"
          ? initialPerguntaValor
          : undefined,
      initialPerguntaOpcaoIds:
        tipoSecao === "pergunta" || tipoSecao === "pergunta-com-upload"
          ? initialPerguntaOpcaoIds
          : undefined,
      initialArquivosIds:
        tipoSecao === "upload" || tipoSecao === "pergunta-com-upload"
          ? initialArquivosIds
          : undefined,
      initialArquivoName:
        tipoSecao === "upload" || tipoSecao === "pergunta-com-upload"
          ? initialArquivoName
          : undefined,
      initialArquivoNomeExibicao:
        tipoSecao === "upload" || tipoSecao === "pergunta-com-upload"
          ? initialArquivoNomeExibicao
          : undefined,
      initialObservacao:
        tipoSecao === "upload" || tipoSecao === "pergunta-com-upload"
          ? initialObservacao
          : undefined,
      canEditPergunta: (campoPergunta as any)?.podeEditar !== false,
      canEditUpload: (campoUpload as any)?.podeEditar !== false,
      canEditObservacao: (campoObservacao as any)?.podeEditar !== false,
      accept: (() => {
        const normalize = (s: unknown) =>
          String(s || "")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase();

        const titulo = normalize((criterio as any)?.titulo);
        const isEmpresasAssociadas =
          titulo.includes("empresas-associadas") || titulo.includes("relacao");

        return isEmpresasAssociadas ? ".xlsx" : ".pdf";
      })(),
      instrucaoUpload:
        tipoSecao === "upload" || tipoSecao === "pergunta-com-upload"
          ? undefined
          : undefined,
      maxSizeMB:
        extrairMaxSizeMB((campoUpload as any) ?? (criterio as any)) ??
        extrairMaxSizeMB(perguntaPrincipal as any) ??
        extrairMaxSizeMB(criterio as any) ??
        undefined,
    };

    secoes.push(secao);
  });

  return secoes;
}


export function aplicarRestricaoAdequacaoEmSecoes(
  secoes: SecaoDocumento[],
  restricao: Set<number>,
): void {
  for (const s of secoes) {
    const cid = s.criterioSolicitacaoId;
    if (cid == null || !Number.isFinite(cid)) continue;
    if (restricao.has(cid)) {
      // Na adequação pendente, os critérios liberados voltam a ser editáveis
      // para pergunta/upload/observação, independentemente do podeEditar original.
      s.canEditPergunta = true;
      s.canEditUpload = true;
      s.canEditObservacao = true;
    } else {
      s.canEditPergunta = false;
      s.canEditUpload = false;
      s.canEditObservacao = false;
    }
  }
}

export function mapSolicitacaoDetalheToSecoes(
  detalhe: SolicitacaoDetalhe,
  options?: { incluirCriteriosSomenteAnalista?: boolean },
): {
  obrigatorios: SecaoDocumento[];
  bonus: SecaoDocumento[];
  bonusExtra: SecaoDocumento[];
} {
  const grupos = (detalhe as any).grupos || [];

  const obrigatorios: SecaoDocumento[] = [];
  const bonus: SecaoDocumento[] = [];
  const bonusExtra: SecaoDocumento[] = [];

  grupos.forEach((grupo: GrupoDetalhe) => {
    const tipoGrupo = normalizarTituloGrupo((grupo as any).titulo);
    const secoesGrupo = mapGrupoToSecoes(grupo, options);

    if (tipoGrupo === "bonus") {
      bonus.push(...secoesGrupo);
    } else if (tipoGrupo === "bonus-extra") {
      bonusExtra.push(...secoesGrupo);
    } else {
      obrigatorios.push(...secoesGrupo);
    }
  });

  const adequacaoPendente =
    Boolean(detalhe.adequacaoSolicitadaEm) && !detalhe.adequacaoRealizadaEm;
  const liberados = detalhe.criteriosLiberadosParaAdequacaoIds;
  if (adequacaoPendente && Array.isArray(liberados)) {
    const set = new Set(liberados);
    aplicarRestricaoAdequacaoEmSecoes(obrigatorios, set);
    aplicarRestricaoAdequacaoEmSecoes(bonus, set);
    aplicarRestricaoAdequacaoEmSecoes(bonusExtra, set);
  }

  return { obrigatorios, bonus, bonusExtra };
}
